/**
 * my-custom-product.js
 * 产品页自定义功能主入口（整合旧 my-custom-template-product.js）
 *
 * 模块说明：
 *  1. 工具函数                — DOM 快捷访问、格式化、设备判断
 *  2. 状态管理                — 当前变体 ID / 对象 / 可用性
 *  3. 变体库存同步             — 从 DOM 隐藏节点读取各变体真实库存
 *  4. 折扣徽章 UI             — #myProdDCConfigDirectNumberId 等元素
 *  5. 折扣 Banner UI          — .my-product-dcbanner-* 区域
 *  6. 公共配置标签             — configLabels 动态渲染
 *  7. 积分展示                — 根据变体 ID 更新积分数字
 *  8. 预售信息工具             — 全局函数 myCustomGetPresaleDataStrFn
 *  9. 变体切换监听             — Dawn PubSub / MutationObserver 降级
 * 10. 预计到货日期             — 工具函数，挂 window
 * 11. 四个官网信息弹窗         — 打开 / 关闭 / 左右翻页 / 触摸滑动
 * 12. 功能配置弹窗             — .my-custom-product-func-configwrap
 * 13. 描述折叠                — 全局 myCustomProdDescItemClick
 * 14. Gift 图标位置调整        — 将 sca-gift-icon 移入折扣区
 * 15. AddOn（追加选购）        — 数据初始化 / 选中状态 / 弹窗 / 折扣 / 加购接口
 * 16. 维保 & 保险（WB/BX）    — 状态管理 / 变体切换 / 弹窗 / 协议确认
 * 17. Gift 礼物弹窗           — 全局 myCustomGiftGiveDialogShow
 * 18. 初始化                  — DOMContentLoaded
 *
 * 依赖（由 main-product.liquid <script> 注入到 window）：
 *  - window.myCustomSaveProdId          产品 ID
 *  - window.myCustomSaveVariantId       当前变体 ID
 *  - window.myCustomProdSaveObj         Shopify product JSON
 *  - window.myProdDCConfigVariantObj    折扣配置（key = 变体 ID）
 *  - window.myProdDCConfigCustomObj     全局折扣配置（key = 产品 ID）
 *  - window.myProdCommonConfigVariantObj 公共配置（包含 configLabels）
 *  - window.myProdPresaleConfigVariantObj 预售配置（key = 变体 ID）
 *  - window.myCustomCartUpcartObj       维保/保险数据（外部文件注入）
 *
 * Dawn 兼容：
 *  使用 Dawn 全局 PubSub（subscribe / PUB_SUB_EVENTS）监听变体切换，
 *  不可用时退回 MutationObserver 监听 input.product-variant-id
 */

'use strict';

/* ═══════════════════════════════════════════════
   §1  工具函数
   ═══════════════════════════════════════════════ */

/** getElementById 简写 */
const _el = (id) => document.getElementById(id);
const _elclass = (cls) => document.querySelector(`.${cls}`);
const _setTxt = (type, name, val) => { const e = type=== 'id' ? _el(name) : _elclass(name); if (e) e.textContent = val; };
const _setHtml = (type,name, val) => { const e = type=== 'id' ? _el(name) : _elclass(name); if (e) e.innerHTML = val; };

/**
 * 切换 CSS 隐藏类
 * @param {HTMLElement|null} el
 * @param {boolean} hidden  true = 隐藏
 */
const _setHidden = (el, hidden) => {
  el?.classList.toggle('myCustomNoShow', hidden);
};

/**
 * 数字千位分隔格式化
 * @param {number|string} num
 * @returns {string}
 */
const formatNumber = (num) =>
  String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** 是否移动端宽度（< 767px） */
const isMobile = () => window.innerWidth < 767;

// 发货日期
const getMyDeliveryDateFn = () =>{
    let dateStr = ''
    let myDate = new Date()
    let myYMD = myDate.toLocaleDateString() // '2023/10/11'
    let myZJ = myDate.getDay() > 0 ? myDate.getDay() : '7'
    let myHour = myDate.getHours()
    let addDayFn = function (day) {
        // console.log(day, '算 几天后')

        let ndd = new Date()
        ndd.setDate(ndd.getDate() + day)
        // console.log(ndd, 'ndd 复制几天后')
        let yy = ndd.getFullYear()
        let mm = ndd.getMonth() < 9 ? '0' + (ndd.getMonth() + 1) : (ndd.getMonth() + 1)
        let d = ndd.getDate() < 10 ? '0' + ndd.getDate() : ndd.getDate()
        // console.log(yy, mm, d, 'yy, mm, d')

        return yy + '/' + mm + '/' + d
    }
    // 如果是周五-周日
    if (myZJ - 5 >= 0) {
        let num = 7 - myZJ + 1
        dateStr = addDayFn(num)
    } else {
        // 周一到周四
        dateStr = addDayFn(1)
    }
    return dateStr
}

/** start-- 当前预售相关 预售时间和文案 */
const getPresaleDataStr = (variantId, type) => {
  const presaleObj = window.myProdPresaleConfigVariantObj ?? {};
  const suffix = type === '1' ? ' より順次発送予定' : '';
  return presaleObj[variantId] ? presaleObj[variantId] + suffix : '';
};
window.myCustomGetPresaleDataStrFn = getPresaleDataStr;
/** end-- 当前预售相关 */

/* ── AddOn 加购：发送 /cart/add.js 请求 ── */
const addToCart = (formData) => {
  fetch('/cart/add.js', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(formData),
  })
    .then((res) => res.json())
    .then(() => { /* 成功后可触发购物车更新事件 */ })
    .catch((err) => console.error('加入购物车失败:', err));
};

/** 安全执行单个初始化函数：抛错时仅记录日志，不中断同批次的后续函数
 *  （尤其保证加购按钮刷新 initChangeForXFAndFormBtnFn 不会被前置函数的异常拖累而哑火） */
const mySafeRun = (fn, label) => {
  try { fn(); } catch (err) { console.error(`[myCustomProd] ${label} 执行异常：`, err); }
};

/** 监听变体 ID 变化 */
const watchVariantIdChange = () => {
  document.addEventListener('myOptionChange', (detail) => {
    console.log('监听到 myOptionChange 事件，新的变体 ID:', detail);
    console.log('当前 options:', optionPicker);

    // 各初始化函数用 mySafeRun 隔离，任一抛错都不影响其它 UI 与加购按钮刷新
    // 这里是执行 根据变体id 产品图片展示不同的
    mySafeRun(() => MyCustomProductSliderConstructor.myProdSliderVariantForImg(optionPicker.currentVariantId), 'myProdSliderVariantForImg');

    mySafeRun(() => myProdDCCOnfig.watchVariantChange(), 'myProdDCCOnfig.watchVariantChange'); // 触发折扣配置更新等相关逻辑
    mySafeRun(() => myPointUnitConfig.watchVariantChange(), 'myPointUnitConfig.watchVariantChange'); // 触发积分单位配置更新
    mySafeRun(() => updateVariantPriceUI(), 'updateVariantPriceUI'); // 价格随变体切换更新
    mySafeRun(() => myCustomMsrpVariantChangeFn(), 'myCustomMsrpVariantChangeFn'); // 触发 MSRP 计算更新
    mySafeRun(() => myCustomFuncConfigStartFn(), 'myCustomFuncConfigStartFn'); // 触发功能配置相关逻辑

    // 维保保险相关
    setTimeout(() => window.myCustomWBVariantIdChangeFn(), 500);
    // egift 相关初始化
    mySafeRun(() => myCustomGiftVariantChangeFn(), 'myCustomGiftVariantChangeFn');


    mySafeRun(() => initChangeForXFAndFormBtnFn(), 'initChangeForXFAndFormBtnFn'); // 触发悬浮加购按钮和表单加购按钮的变体切换逻辑

  });
}

/**
 * 页面初始加载时的逻辑
 */
document.addEventListener('DOMContentLoaded', () => {
  loadVariantInventoryFromDOM(); // 从 DOM 加载变体库存数据到全局对象
  watchVariantIdChange();
  isMobile() && (myProdGBConfig.isPhoneWidth = true);

  // initChangeForXFBtnFn(); // 初始化悬浮加购按钮的变体切换逻辑
  
  setTimeout(() => {
    // 各初始化函数用 mySafeRun 隔离，任一抛错都不影响其它 UI 与加购按钮刷新
    mySafeRun(() => myProdDCCOnfig.watchVariantChange(), 'myProdDCCOnfig.watchVariantChange'); // 触发折扣配置更新等相关逻辑
    mySafeRun(() => myPointUnitConfig.watchVariantChange(), 'myPointUnitConfig.watchVariantChange'); // 触发积分单位配置更新
    mySafeRun(() => updateVariantPriceUI(), 'updateVariantPriceUI'); // 价格随变体切换更新
    mySafeRun(() => myCustomMsrpVariantChangeFn(), 'myCustomMsrpVariantChangeFn'); // 触发 MSRP 计算更新
    mySafeRun(() => myCustomFuncConfigStartFn(), 'myCustomFuncConfigStartFn'); // 触发功能配置相关逻辑

    // 评论插件icon替换
    mySafeRun(() => myCustomReviewPluginIconReplaceFn(), 'myCustomReviewPluginIconReplaceFn');

    // egift 相关初始化
    mySafeRun(() => myCustomGiftVariantChangeFn(), 'myCustomGiftVariantChangeFn');

    mySafeRun(() => initChangeForXFAndFormBtnFn(), 'initChangeForXFAndFormBtnFn'); // 触发悬浮加购按钮和表单加购按钮的变体切换逻辑


  })
  // 初始化 选项相关
  // _initOption();
  // 初始化 加购按钮相关
  // initMyAddCartFn();

  // 初始化 AddOn 追加选购功能
  addonInit(); 

  // 维保保险相关初始化
  initWBAndBX();
  setTimeout(() => window.myCustomWBVariantIdChangeFn(), 500);

  // 四个弹窗
  initFourInfoDialog();

  // 产品系列左右切换
  initProductSeriesSliderFn();

});

const loadVariantInventoryFromDOM = () => {
  const nodes = document.querySelectorAll('.myProductAddOnSaveVariantNumId-item');
  if (!nodes.length) return;

  const inventoryMap = {};
  nodes.forEach((node) => {
    const [id, qty] = node.textContent.trim().split(':');
    if (id) inventoryMap[id] = Number(qty) || 0;
  });

  const prod = window.myCustomProdSaveObj;
  if (!prod?.variants) return;
  prod.save_variant_inventory_quantity_obj = inventoryMap;
  prod.variants.forEach((v) => {
    v.inventory_quantity = inventoryMap[v.id] ?? 0;
  });
};



/* ═══════════════════════════════════════════════
   §2  状态管理
   ═══════════════════════════════════════════════ */

/** 当前变体的全局状态（统一读写，避免多处散乱引用 window 变量） */
var myProdGBConfig = {
  priceUnit: '¥',
  isPhoneWidth: false, // 在宽度上判断
};



/** start-- 当前折扣相关 */

// myProdDCConfigVariantObj 是 此产品的 整个折扣配置
var myProdDCCOnfig = {
  curVaraintConfigInfo: {},
  direct:{
    counponkey: null,
    discountEndTime: null,
  }, // 当前变体的 直接折扣 key
  point: {
    counponkey: null,
    number: null,
    desc: null,
  }, // 当前变体的 积分折扣 key
  watchVariantChange: () => {
    // 监听变体切换事件，更新当前变体的折扣配置
    const variantId = optionPicker?.currentVariantId;
    myProdDCCOnfig.getCurVariantDcConfig(variantId);
    myProdDCCOnfig.updateDCDirectUI();
    myProdDCCOnfig.updateDCPointUI();
  },
  getCurVariantDcConfig: (variantId) => {
    // 在这里实现获取当前变体折扣配置的逻辑
    if (!variantId) return {};
    // 防御：全局 myProdDCConfigVariantObj 可能未输出（无折扣配置的商品），裸读会抛 ReferenceError
    myProdDCCOnfig.curVaraintConfigInfo = window.myProdDCConfigVariantObj?.[variantId] || {};
    myProdDCCOnfig.direct.counponkey = myProdDCCOnfig.curVaraintConfigInfo.direct?.counponkey || null;
    myProdDCCOnfig.direct.discountEndTime = myProdDCCOnfig.curVaraintConfigInfo.direct?.endtime ? new Date(myProdDCCOnfig.curVaraintConfigInfo.direct.endtime) : null;
    myProdDCCOnfig.point.counponkey = myProdDCCOnfig.curVaraintConfigInfo.point?.counponkey || null;
    myProdDCCOnfig.point.number = myProdDCCOnfig.curVaraintConfigInfo.point?.number || null;
    myProdDCCOnfig.point.desc = myProdDCCOnfig.curVaraintConfigInfo.point?.desc || null;
  },
  updateDCDirectUI: () => {
    let el_price1 = _elclass('my-product-dcbanner-wrap');
    let el_price2 = _elclass('my-product-price-wrap');

    if (!myProdDCCOnfig.direct.counponkey) {
      // 如果没有直接折扣，隐藏相关 UI 元素
      // my-product-dcbanner-wrap
      el_price1?.classList.add('myCustomNoShow');
      el_price2?.classList.remove('myCustomNoShow');

      return
    };
    // 更新相关折扣 UI 的显示状态和内容
    // combo选项中的？？

    // 产品价格旁边的 ID myProductDcBannerNumID
    _setTxt('id', 'myProductDcBannerNumID', myProdDCCOnfig.direct.counponkey || '');
    // form 加购按钮 my-custom-product-form-btn-dcwrap
    _setTxt('class', 'my-custom-product-form-btn-dcwrap', myProdDCCOnfig.direct.counponkey || '');
    // 悬浮加购按钮 ID myXfPcProdDcId  myXfMbProdDcId
    _setTxt('id', 'myXfPcProdDcId', myProdDCCOnfig.direct.counponkey || '');
    _setTxt('id', 'myXfMbProdDcId', myProdDCCOnfig.direct.counponkey || '');

    el_price1?.classList.remove('myCustomNoShow');
    el_price2?.classList.add('myCustomNoShow');

  },
  updateDCPointUI: () => {
    // 更新积分折扣相关 UI 的显示状态和内容

    // 1. 功能区的 积分打折 ID myProdDCConfigPointDescId
    let pointId = _el('myProdDCConfigPointDescId');
    if (!pointId) return;

    if (myProdDCCOnfig.point.desc) {
      console.log('显示积分折扣描述:', myProdDCCOnfig.point.desc);
      let el = _elclass('my-product-func-itemwrap.pointdc');
      el?.classList.remove('myCustomNoShow');
      _setTxt('id', 'myProdDCConfigPointDescId', myProdDCCOnfig.point.desc || '');
      pointId.setAttribute('data-counponkey', myProdDCCOnfig.point.counponkey || '');
    } else {
      let el = _elclass('my-product-func-itemwrap.pointdc');
      el?.classList.add('myCustomNoShow');
      _setTxt('id', 'myProdDCConfigPointDescId', '');
      pointId.removeAttribute('data-counponkey');
    }

  },
}
/** end-- 当前折扣相关 */



/** start-- 当前顶部标签相关 */
const _initConfigLabels = () =>{
  _el('myProdDCConfigDirectLabelId')?.classList.add('myCustomProdDCNoShow');
  const configLabels = myProdCommonConfigVariantObj.configLabels;
  if (!configLabels) return;
  let configLabelsArr = configLabels.split(',');
  let resHtml = ''
  if (configLabelsArr && configLabelsArr.length) {
      configLabelsArr.forEach((item, index) => {
          resHtml += `<div style="margin-right:4px;padding: 4px 10px;border: 1px solid #e0393a; border-radius: 4px;">${item}</div>`
      });
      _setHtml('id', 'myProdDCConfigDirectLabelId', resHtml);
      _el('myProdDCConfigDirectLabelId')?.classList.remove('myCustomProdDCNoShow');
  }
}
/** end-- 当前顶部标签相关 */

/** start-- 功能模块第一个积分计算相关 */
var myPointUnitConfig = {
  config: {},
  pointPerUnit: 4,
  unit: null,
  watchVariantChange: () => {
    myPointUnitConfig.getData();
    // 监听变体切换事件，更新当前变体的积分单位配置
    const variantId = optionPicker?.currentVariantId;
    console.log('监听到变体切换，当前变体 ID:', variantId);
    myPointUnitConfig.getCurVariantPointUnit(variantId);
    myPointUnitConfig.updatePointUnitUI();
  },
  getData: () => {
    let el = _el('myProductSaveVariantPointId');
    console.log('读取积分单位配置 DOM:', el);
    if (!el) return;
    let config = {};
    el.querySelectorAll('div').forEach(div => {
      let [variantId, pointUnit] = div.textContent.split(':');
      config[variantId] = Number(pointUnit);
    });
    console.log('积分单位配置:', config);
    myPointUnitConfig.config = config;
  },
  getCurVariantPointUnit: (variantId) => {
    if (!variantId) return null;
    myPointUnitConfig.unit = myPointUnitConfig.config[variantId] || 0;
  },
  updatePointUnitUI: () => {
    // 更新相关积分单位 UI 的显示状态和内容
    // 1. 功能区的 积分单位 ID myProductVariantPointId myProductVariantPointMoney
    _setTxt('id', 'myProductVariantPointId', myPointUnitConfig.unit || 0);
    _setTxt('id', 'myProductVariantPointMoney', myPointUnitConfig.unit * myPointUnitConfig.pointPerUnit || 0);
  },
}
/** end-- 当前积分计算相关 */

/** start-- 变体价格展示（値引き前価格 / 普通価格）随变体切换更新
 *  两处价格 DOM 的 id 重复（myProductDCBannerPriceId），用 class .my-product-dcbanner-price 遍历更稳妥 */
const updateVariantPriceUI = () => {
  const price = optionPicker?.currentVariant?.price;
  if (price === undefined || price === null) return;
  // currentVariant.price 单位为最小货币单位（cents，×100），/100 转为日元展示（与 MSRP 逻辑一致）
  const priceText = `${myProdGBConfig.priceUnit}${formatNumber(Math.round(price / 100))}`;
  document.querySelectorAll('.my-product-dcbanner-price').forEach((el) => {
    el.textContent = priceText;
  });
};
/** end-- 变体价格展示 */

/** start-- 功能模块中MSRP */
const myCustomMsrpVariantChangeFn = () => {
    let variantId = optionPicker?.currentVariantId;
    console.log('myCustomMsrpVariantChangeFn，当前变体 ID:', variantId);
    if (!variantId) return;
    // 获取当前变体的价格（JPY，单位为日元）
    let curVariantObj = optionPicker?.currentVariant || {};
    let price = curVariantObj.price;
    console.log('当前变体价格（JPY，单位为日元）:', price);
    if (!price) return;

    let discount = 0;
    let type = 'none';
    let variantConfigObj = myProdDCCOnfig.curVaraintConfigInfo;
    // direct 折扣
    console.log(variantConfigObj, '当前变体的折扣配置对象');
    if (variantConfigObj && variantConfigObj['direct'] && variantConfigObj['direct'].counponkey) {
        // 防御：number 可能为 undefined/null 或数字类型，直接 .indexOf 会抛错；统一转字符串处理
        let number = String(variantConfigObj['direct'].number ?? '');
        if (number.indexOf('%') > -1) {
            type = 'percent';
            discount = parseFloat(number.replace(/[^0-9.-]+/g, '')) || 0;
        } else {
            type = 'direct';
            discount = parseFloat(number.replace(/[^0-9.-]+/g, '')) || 0;
        }
    }
    console.log(price, discount, type, '当前变体的价格、折扣和类型');
    myCustomMsrpMoneyGetFn(price, discount, type);
}
const myCustomMsrpMoneyGetFn = (price, discount, type) => {
    // console.log(price, discount, type, 'myCustomMsrpMoneyGetFnprice, discount, type');
    // 不打折：price / 24；有折扣（如20%OFF）：price * (1 - discount/100) / 24
    // 结果向上取整后格式化展示到 myCustomProductMsrpMoneyId
    let msrpDom = document.getElementById('myCustomProductMsrpMoneyId');
    if (!msrpDom) return;

    let msrpMoney = 0;
    price = price / 100; // 转换为正常的价格
    // console.log(price, discount, type, 'myCustomMsrpMoneyGetFnpricepricepriceprice');
    if (type === 'direct') {
        msrpMoney = price - discount;
    } else if (type === 'percent') {
        msrpMoney = price * (1 - discount / 100);
    } else {
        msrpMoney = price;
    }
    msrpMoney = msrpMoney / 24;
    msrpMoney = Math.ceil(msrpMoney);
    msrpDom.innerText = formatNumber(msrpMoney);
}
/** end-- 功能模块中MSRP */

/** start-- 功能模块中自定义功能配置 */
const myCustomFuncConfigStartFn = () => {
  document.querySelectorAll('.my-product-func-itemwrap.custom').forEach(el => {
    el.addEventListener('click', () => {
      console.log('点击了自定义功能配置项，元素:', el);
      const dialogDom = el.parentElement?.querySelector('.my-product-func-dialogwrap');
      if (!dialogDom) return;
      const data_save_url = el.getAttribute('data-save-url');
      const data_type = el.getAttribute('data-type');
      if (data_type === 'link') {
        window.open(data_save_url, '_blank');
      } else if (data_type === 'dialog_qa') {
        dialogDom.querySelector('.my-custom-product-func-dialogcontwrap')?.classList.add('scrollY');
        dialogDom.style.display = 'block';
      } else {
        if (data_type === 'dialog_link') {
          const iframe = dialogDom.querySelector('.my-custom-product-func-dialog-iframewrap > iframe');
          if (iframe) iframe.src = data_save_url;
        } else if (data_type === 'dialog_img') {
          const img = dialogDom.querySelector('.my-custom-product-func-dialog-imgwrap > img');
          if (img) img.src = data_save_url;
        }
        dialogDom.style.display = 'block';
      }
    });
  });

  document.querySelectorAll('.my-custom-product-func-dialog-closebtn, .my-custom-product-func-dialog-closebtn-qa')
    .forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.parentElement?.parentElement) btn.parentElement.parentElement.style.display = 'none';
      });
    });
}
/** end-- 功能模块中自定义功能配置 */

/** start-- 描述折叠事件 */
const myCustomProdDescItemClick = (titleEl) => {
  if (!titleEl) return;
  const clickedItem = titleEl.closest('.my-product-desc-itemwrap');
  if (!clickedItem) return;

  const isOpen = titleEl.classList.contains('custom-prod-desc-item-title-active');

  document.querySelectorAll('.my-product-desc-itemwrap').forEach(item => {
    const title = item.querySelector('.my-product-desc-item-title');
    const svg   = item.querySelector('.my-product-desc-item-svg');
    const cont  = item.querySelector('.my-product-desc-item-cont');
    if (item === clickedItem) {
      title?.classList.toggle('custom-prod-desc-item-title-active', !isOpen);
      svg?.classList.toggle('custom-prod-desc-item-svg-up', !isOpen);
      cont?.classList.toggle('custom-prod-desc-item-cont-show', !isOpen);
    } else {
      title?.classList.remove('custom-prod-desc-item-title-active');
      svg?.classList.remove('custom-prod-desc-item-svg-up');
      cont?.classList.remove('custom-prod-desc-item-cont-show');
    }
  });
}
/** end-- 描述折叠事件 */

/** start-- 描述列表展开/收起（超过3个时，默认只展示前3个） */
const myCustomProdDescToggleFn = (toggleEl) => {
  if (!toggleEl) return;
  const wrap = toggleEl.closest('.my-product-desc-wrap');
  if (!wrap) return;
  // 切换展开状态：展开后显示全部，收起后只显示前3个
  const expanded = wrap.classList.toggle('my-product-desc-expanded');
  const textEl = toggleEl.querySelector('.my-product-desc-toggle-text');
  if (textEl) textEl.textContent = expanded ? '閉じる' : 'もっと見る';
}
/** end-- 描述列表展开/收起 */


/** start-- 产品系列左右切换 */
const initProductSeriesSliderFn = () => {
  document.querySelectorAll('.my-product-series-contentwrap').forEach((wrap) => {
    const track   = wrap.querySelector('.my-product-series-itemwrap');
    const prevBtn = wrap.querySelector('.my-product-series-changebtn.prev');
    const nextBtn = wrap.querySelector('.my-product-series-changebtn.next');
    if (!track || !prevBtn || !nextBtn) return;

    const items = track.querySelectorAll('.my-product-series-item');
    if (!items.length) return;

    // 单步滚动距离 = 单个 item 宽度 + 间距
    const getStep = () => {
      const gap = parseFloat(getComputedStyle(track).columnGap) || parseFloat(getComputedStyle(track).gap) || 0;
      return items[0].getBoundingClientRect().width + gap;
    };
    // 最大可滚动距离（内容总宽 - 可视宽度），内容未超出时为 0
    const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    // 根据当前 scrollLeft 处理左右临界（按钮禁用/隐藏）
    const apply = () => {
      const max = getMaxScroll();
      // 内容未超出可视区（如 1~2 个 item）：无需切换，隐藏左右按钮
      if (max <= 1) {
        prevBtn.style.display = 'none';
        nextBtn.style.display = 'none';
        return;
      }
      prevBtn.style.display = '';
      nextBtn.style.display = '';
      // 到达左/右临界时禁用对应按钮（留 1px 容差应对亚像素）
      const sl = track.scrollLeft;
      prevBtn.classList.toggle('is-disabled', sl <= 0);
      nextBtn.classList.toggle('is-disabled', sl >= max - 1);
    };

    prevBtn.addEventListener('click', () => {
      // 已到最左：不处理（按钮仍拦截点击，避免穿透到下方的系列链接触发跳转）
      if (prevBtn.classList.contains('is-disabled')) return;
      track.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', () => {
      // 已到最右：同上，拦截点击避免穿透跳转
      if (nextBtn.classList.contains('is-disabled')) return;
      track.scrollBy({ left: getStep(), behavior: 'smooth' });
    });

    // 触摸滑动 / 惯性滚动时同步按钮状态
    track.addEventListener('scroll', apply, { passive: true });
    // 响应式：尺寸变化后重新计算，防止越界
    window.addEventListener('resize', apply);

    apply();
  });
}
/** end-- 产品系列左右切换 */


/** start-- 维保保险相关 */
window.myCustomProdWBSaveObj = {
  WBStatus: false,  // 维保选中状态
  curWBObj: { id: '', price: '', link: '' },
  BXStatus: false,  // 保险选中状态
  curBXObj: { id: '', price: '', link: '' },
};
const wbBxDialogCheckState = { isWBCheck: false, isBXCheck: false };
// 控制维保/保险弹窗显示
const toggleWBBXDialog = (type, show) => {
  const id = type === 'wb' ? 'myCustomProdAddwrapWbdialog' : 'myCustomProdAddwrapBxdialog';
  _el(id)?.classList.toggle('show', show);
};
// 切换维保/保险选中状态
const toggleWBItemStatus = (eleId, statusKey) => {
  const el = _el(eleId);
  if (!el) return;
  const newStatus = !window.myCustomProdWBSaveObj[statusKey];
  window.myCustomProdWBSaveObj[statusKey] = newStatus;
  el.classList.toggle('active', newStatus);
  el.querySelector('.my-custom-wb-item-addwrap-leftstatus')?.classList.toggle('active', newStatus);
  // window.myCustomProductAddCartClassConstructor?.addWBOrBXEventListenerFn?.();
  initChangeForXFAndFormBtnFn();
};
// 初始化维保/保险模块（DOMContentLoaded 后调用
const initWBAndBX = () => {
  const wbEl = _el('myCustomWBItemAddOne');
  const bxEl = _el('myCustomWBItemAddTwo');

  const handleWBClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.myCustomProdWBSaveObj.WBStatus
      ? toggleWBItemStatus('myCustomWBItemAddOne', 'WBStatus')
      : toggleWBBXDialog('wb', true);
  };
  const handleBXClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.myCustomProdWBSaveObj.BXStatus
      ? toggleWBItemStatus('myCustomWBItemAddTwo', 'BXStatus')
      : toggleWBBXDialog('bx', true);
  };

  wbEl?.addEventListener('click', handleWBClick);
  wbEl?.querySelector('.my-custom-wb-item-addwrap-leftstatus')?.addEventListener('click', handleWBClick);
  bxEl?.addEventListener('click', handleBXClick);
  bxEl?.querySelector('.my-custom-wb-item-addwrap-leftstatus')?.addEventListener('click', handleBXClick);

  // 详情链接（在新标签打开）
  const openDetailLink = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const href = e.currentTarget.dataset.href;
    if (href) window.open(href);
  };
  wbEl?.querySelector('.my-custom-wb-item-addwrap-rightdetail')?.addEventListener('click', openDetailLink);
  bxEl?.querySelector('.my-custom-wb-item-addwrap-rightdetail')?.addEventListener('click', openDetailLink);

  // 若当前产品本身就是维保/保险商品，隐藏加购区
  const productId = String(window.myCustomProdSaveObj?.id ?? '');
  if (productId === '8284333146287') {
    setTimeout(() => {
      document.querySelector('.shopify-payment-button')?.replaceChildren();
      document.querySelector('.product-template-my-AddOn-form-right-btnwrap')?.replaceChildren();
      document.querySelector('.product-template-my-AddOn-form-wrap')
               ?.querySelector('.product-template-my-AddOn-form-mb-right-btn')?.remove();
    });
  }

  // 初始化协议弹窗事件
  initWBBXDialogEvents();
};
// 绑定维保/保险协议弹窗中的按钮和 checkbox 事件 
const initWBBXDialogEvents = () => {
  const setupDialog = (type, eleId, statusKey, checkKey) => {
    const dialogEl = _el(type === 'wb' ? 'myCustomProdAddwrapWbdialog' : 'myCustomProdAddwrapBxdialog');
    if (!dialogEl) return;

    // 取消 / 关闭
    dialogEl.querySelector('.my-custom-prod-addwrap-wbdialog-cont-closewrap')
      ?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleWBBXDialog(type, false);
      });

    // 确认
    dialogEl.querySelector('.my-custom-prod-addwrap-wbdialog-cont-btnconfirmwrap')
      ?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const errWrap = dialogEl.querySelector('.my-custom-prod-addwrap-wbdialog-cont-policyerrwrap');
        if (!wbBxDialogCheckState[checkKey]) {
          errWrap?.classList.add('show');
          return;
        }
        errWrap?.classList.remove('show');
        toggleWBBXDialog(type, false);
        toggleWBItemStatus(eleId, statusKey);
      });

    // Checkbox 状态
    dialogEl.querySelector('input')?.addEventListener('change', function () {
      wbBxDialogCheckState[checkKey] = this.checked;
      const errWrap = dialogEl.querySelector('.my-custom-prod-addwrap-wbdialog-cont-policyerrwrap');
      errWrap?.classList.toggle('show', !this.checked);
    });
  };

  setupDialog('wb', 'myCustomWBItemAddOne', 'WBStatus', 'isWBCheck');
  setupDialog('bx', 'myCustomWBItemAddTwo', 'BXStatus', 'isBXCheck');
};
// 当产品变体切换时，同步维保/保险选项的显示与数据
const updateWBOnVariantChange = (variantId) => {
  if (!variantId) variantId = optionPicker?.currentVariantId;
  if (!variantId) return;
  console.log('updateWBOnVariantChange，当前变体 ID:', variantId);

  const cart = window.myCustomCartUpcartObj;
  if (!cart?.WBZVariantAndNum?.length && !cart?.BXZVariantAndNum?.length) return;

  /* 维保 */
  let curWBObj = {};
  cart.WBZVariantAndNum.forEach((item) => {
    item.zVariantArr?.forEach((v) => {
      if (String(v.variantId) === String(variantId)) curWBObj = item;
    });
  });
  const wbEl = _el('myCustomWBItemAddOne');
  if (curWBObj?.fVariantId && wbEl) {
    wbEl.querySelectorAll('.myCustomProdWBYear').forEach((yearEl) => {
      yearEl.textContent = curWBObj.year ?? '';
    });
    if (curWBObj.price) {
      const priceEl = wbEl.querySelector('.myCustomProdWBPriceId');
      if (priceEl) priceEl.textContent = formatNumber(curWBObj.price);
    }
    wbEl.querySelector('.my-custom-wb-item-addwrap-rightdetail')
        ?.setAttribute('data-href', curWBObj.link);
    wbEl.classList.add('show');
    wbEl.parentElement?.querySelector('.my-custom-prod-wb-addwrap-title')?.classList.add('show');
    window.myCustomProdWBSaveObj.curWBObj = { ...curWBObj };
  } else {
    wbEl?.classList.remove('active', 'show');
    wbEl?.querySelector('.my-custom-wb-item-addwrap-leftstatus')?.classList.remove('active');
  }

  /* 保险 */
  let curBXObj = {};
  cart.BXZVariantAndNum.forEach((item) => {
    item.zVariantArr?.forEach((v) => {
      if (String(v.variantId) === String(variantId)) curBXObj = item;
    });
  });
  const bxEl = _el('myCustomWBItemAddTwo');
  if (curBXObj?.fVariantId && bxEl) {
    if (curBXObj.price) {
      const priceEl = bxEl.querySelector('.myCustomProdWBPriceId');
      if (priceEl) priceEl.textContent = formatNumber(curBXObj.price);
    }
    bxEl.querySelector('.my-custom-wb-item-addwrap-rightdetail')
        ?.setAttribute('data-href', curBXObj.link);
    bxEl.classList.add('show');
    wbEl?.parentElement?.querySelector('.my-custom-prod-wb-addwrap-title')?.classList.add('show');
    window.myCustomProdWBSaveObj.curBXObj = { ...curBXObj };
  } else {
    bxEl?.classList.remove('active', 'show');
    bxEl?.querySelector('.my-custom-wb-item-addwrap-leftstatus')?.classList.remove('active');
  }
};
// 挂到 window，外部脚本（维保加载完成事件）可直接调用
window.myCustomWBVariantIdChangeFn = updateWBOnVariantChange;

// 加购完成后重置维保/保险状态（供外部悬浮购物车调用）
window.myCustomWBAndBxAfterFn = () => {
  window.myCustomProdWBSaveObj.WBStatus = false;
  window.myCustomProdWBSaveObj.BXStatus = false;
  ['myCustomWBItemAddOne', 'myCustomWBItemAddTwo'].forEach((id) => {
    _el(id)?.classList.remove('active');
    _el(id)?.querySelector('.my-custom-wb-item-addwrap-leftstatus')?.classList.remove('active');
  });
};

/** end-- 维保保险相关 */


/** start-- egift插件 */
const myCustomGiftGiveDialogShow = (flag) => {
    console.log(flag, 'myCustomGiftGiveDialogShowflag');
    const dialog = document.querySelector('.my-custom-product-gift-give-info-dialog');
    if (dialog) {
        dialog.classList.toggle('show', flag);
    }
}
// 监听变体变化，进行送礼物内容的展示或隐藏
const myCustomGiftVariantChangeFn = (variantId) => {
    // 如果没有传入 variantId，则取当前默认变体
    if (!variantId) {
        variantId = optionPicker?.currentVariantId;
    }
    if (!variantId || !window.myCustomProductGiftGiveList) return;
    const giftGiveWrapDom = document.querySelector('.my-custom-product-gift-give-wrap');
    const hasGiftGive = window.myCustomProductGiftGiveList.some((item) =>
        item.variants.find(j => j.id + '' === variantId + '')
    );
    if (giftGiveWrapDom) {
        giftGiveWrapDom.classList.toggle('show', hasGiftGive);
    }
}
/** end-- egift插件 */


/** start-- 评论插件的修改 */
const myCustomReviewPluginIconReplaceFn = () => {
    // jdgm-qa-badge__icon
    // 获取评论插件的 icon 的 dom，进行替换
    setTimeout(() => {
        let commentPluginIconDom = document.querySelector('.jdgm-qa-badge__icon');

        if (commentPluginIconDom) {
            commentPluginIconDom.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-chat-left-text-fill" viewBox="0 0 16 16" style="color: #E0393A;">
            <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4.414a1 1 0 0 0-.707.293L.854 15.146A.5.5 0 0 1 0 14.793zm3.5 1a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1zm0 2.5a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1z"/>
            </svg>`;
        }
    }, 1000)
}
/** end-- 评论插件的修改 */


/** start-- 四个信息的弹窗 */
  // #myProductPcFourInfoHelpId        触发按钮
  // #myProductPcFourInfoDialogId      弹窗容器
  // #myProductPcFourInfoDialogCloseId 关闭按钮
  // #myProductPcFourInfoDialogLeft    上一张
  // #myProductPcFourInfoDialogRight   下一张
  // .my-product-pc-fourInfo-dialog-item  轮播项（可多个）

const initFourInfoDialog = () => {
  const helpBtn  = _el('myProductPcFourInfoHelpId');
  const dialog   = _el('myProductPcFourInfoDialogId');
  const closeBtn = _el('myProductPcFourInfoDialogCloseId');
  const prevBtn  = _el('myProductPcFourInfoDialogLeft');
  const nextBtn  = _el('myProductPcFourInfoDialogRight');
  if (!helpBtn || !dialog) return;

  const slides = Array.from(dialog.querySelectorAll('.my-product-pc-fourInfo-dialog-item'));
  let currentIndex = 0;

  /** 显示指定索引的轮播项 */
  const showSlide = (idx) => {
    slides.forEach((s, i) => {
      s.style.display = i === idx ? 'block' : 'none';
    });
  };

  /** 更新移动端小圆点 */
  const updateDots = (idx) => {
    dialog.querySelectorAll('.my-product-mb-fourInfo-dialog-point').forEach((dot, i) => {
      dot.style.background = i === idx ? '#9CA1A4' : '#DADDDF';
    });
  };

  const goTo = (idx) => {
    currentIndex = (idx + slides.length) % slides.length;
    showSlide(currentIndex);
    updateDots(currentIndex);
  };

  // 初始显示第一张
  showSlide(0);

  // 打开弹窗
  helpBtn.addEventListener('click', () => { dialog.style.display = 'block'; });

  // 关闭弹窗（关闭按钮 + 点击蒙层）
  const closeDialog = () => { dialog.style.display = 'none'; };
  closeBtn?.addEventListener('click', closeDialog);
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) closeDialog();
  });

  // 翻页
  prevBtn?.addEventListener('click', () => goTo(currentIndex - 1));
  nextBtn?.addEventListener('click', () => goTo(currentIndex + 1));

  // 触摸滑动（移动端）
  const itemWrap = dialog.querySelector('.my-product-pc-fourInfo-dialog-itemwrap');
  if (itemWrap) {
    let touchStartX = 0;
    itemWrap.addEventListener('touchstart', (e) => {
      touchStartX = Math.floor(e.touches[0].pageX);
    }, { passive: true });
    itemWrap.addEventListener('touchend', (e) => {
      const dx = Math.floor(e.changedTouches[0].pageX) - touchStartX;
      if (dx <= -30) goTo(currentIndex + 1);
      else if (dx >= 30) goTo(currentIndex - 1);
    }, { passive: true });
  }
};

/** end-- 四个信息的弹窗 */


/** start-- 这是扫地机问卷功能的函数 */

/** end-- 这是扫地机问卷功能的函数 */



/** start-- addon相关 只是addon的 不涉及和加购等交互 */
window.myProductAddOnOriginObj        = {};
window.myCurrentProductAddOnObj       = {};
window.myProductVariantAddOnObj       = {};
window.myProductVariantAddOnDialogObj = {};

const addonInit = () => {
  initAddOnProductData();
  initAddOnSelectDefaultVariant();
  bindAddOnOriginCartBtn();
};

// AddOn 工具：读取折扣
const getAddOnDiscount = (prodId, variantId) => {
  return window.myProdDCConfigCustomObj?.[prodId]?.[variantId]?.direct?.number ?? '';
};

// AddOn 初始化：读取页面中所有 AddOn 产品数据
const initAddOnProductData = () => {
  document.querySelectorAll('.myCustomAddOnProductSaveProdItemObj').forEach((el) => {
    const id = el.dataset.id;
    if (!id) return;
    try {
      window.myProductAddOnOriginObj[id] = JSON.parse(el.textContent) || {};
    } catch (_) {}
    window.myProductVariantAddOnObj[id]       = { checkStatus: false, variantId: '', quantity: 1, available: false };
    window.myProductVariantAddOnDialogObj[id] = { variantId: '', quantity: 1, openStatus: false };
  });
};

// AddOn 初始化：设置各 AddOn 默认选中变体
const initAddOnSelectDefaultVariant = () => {
  document.querySelectorAll('.product-template-my-AddOn-prodlist-item-selct-select').forEach((sel) => {
    const prodId    = sel.dataset.prodId;
    const variantId = sel.dataset.varinatId;
    const price     = sel.dataset.variantSingleMp;
    if (!prodId) return;
    window.myProductVariantAddOnObj[prodId] = {
      checkStatus: false,
      variantId:   variantId || '',
      quantity:    1,
      available:   false,
      price:       price || 0,
      directDC:    getAddOnDiscount(prodId, variantId),
    };
    window.myProductVariantAddOnDialogObj[prodId] = {
      variantId:  variantId || '',
      quantity:   1,
      openStatus: false,
      directDC:   getAddOnDiscount(prodId, variantId),
    };
  });
  renderAddOnDiscount(true, 'dialog');
  renderAddOnDiscount(true, 'noDialog');
  renderAddOnDialogCartBtnText(true);
};

// AddOn 渲染：折扣标签
const renderAddOnDiscount = (isAll, type, singleProdId) => {
  const selector = type === 'dialog'
    ? '.product-template-my-AddOn-dialog-dc-wrap'
    : '.product-template-my-AddOn-dc-wrap';
  const dataObj = type === 'dialog'
    ? window.myProductVariantAddOnDialogObj
    : window.myProductVariantAddOnObj;

  document.querySelectorAll(selector).forEach((el) => {
    const id = el.dataset.id;
    if (!isAll && id !== singleProdId) return;
    const dc = dataObj[id]?.directDC;
    if (dc) {
      el.textContent = dc + 'クーポンあり';
      el.classList.add('show');
      if (type === 'dialog') {
        el.parentElement?.querySelector('.myAddOnDialogProdDCConfigDirectDescId')?.style.setProperty('display', 'inline-block');
      }
    } else {
      el.textContent = '';
      el.classList.remove('show');
      if (type === 'dialog') {
        el.parentElement?.querySelector('.myAddOnDialogProdDCConfigDirectDescId')?.style.setProperty('display', 'none');
      }
    }
  });
};

// AddOn 渲染：弹窗加购按钮文
const renderAddOnDialogCartBtnText = (isAll, singleProdId) => {
  document.querySelectorAll('.product-template-my-AddOn-prodlist-dialog-add').forEach((btn) => {
    const id = btn.dataset.id;
    if (!isAll && id !== singleProdId) return;
    const dc = window.myProductVariantAddOnDialogObj[id]?.directDC;
    btn.querySelector('.product-template-my-AddOn-prodlist-dialog-add-dc').textContent   = dc ? dc : '';
    btn.querySelector('.product-template-my-AddOn-prodlist-dialog-add-text').textContent = dc
      ? 'を適用してカートに追加'
      : 'カートに追加';
  });
};

// 全局 AddOn 事件函数（HTML inline onclick 调用）
// 外部 AddOn 列表项点击（切换选中）
window.myCustomProdAddOnItemClickFn = (dom) => {
  const id = dom.dataset.id;
  if (!window.myProductVariantAddOnObj[id]) return;
  const isChecked = window.myProductVariantAddOnObj[id].checkStatus;
  window.myProductVariantAddOnObj[id].checkStatus = !isChecked;
  dom.classList.toggle('my-AddOn-prodlist-item-active', !isChecked);
  // 通知悬浮购物车重新计算（外部 class 实例）
  // window.myCustomProductAddCartClassConstructor?.addAddOnEventListenerFn?.();
  initChangeForXFAndFormBtnFn(); // 触发悬浮加购按钮和表单加购按钮的变体切换逻辑
};

// 外部 AddOn 变体 select 展开（阻止冒泡）
window.myCustomAddOnProdItemSelectVariantFn = (_dom, event) => {
  event.stopPropagation();
};

// 外部 AddOn 变体切换
window.myAddOnCustomOnChangeVariant = (variantId, dom) => {
  const selected = dom.options[dom.selectedIndex];
  if (!selected) return;

  const prodId       = selected.dataset.prodId;
  const imgSrc       = selected.dataset.imgsrc;
  const price        = selected.dataset.variantSingleMp;
  console.log('AddOn 变体切换，选中变体 ID:', variantId, '价格:', price);
  // 更新图片 & 价格
  const item = dom.closest('.product-template-my-AddOn-prodlist-item');
  item?.querySelector('.product-template-my-AddOn-prodlist-itemimg img')
      ?.setAttribute('src', '/cdn/shop/' + imgSrc);
  item?.querySelector('.product-template-my-AddOn-prodlist-item-price-red')
      && (item.querySelector('.product-template-my-AddOn-prodlist-item-price-red').textContent = price);

  if (!window.myProductVariantAddOnObj[prodId] || !window.myProductAddOnOriginObj[prodId]) return;
  const variant = window.myProductAddOnOriginObj[prodId].variants
    ?.find((v) => String(v.id) === String(variantId));
  if (!variant) return;

  const obj = window.myProductVariantAddOnObj[prodId];
  obj.variantId = variantId;
  obj.price     = variant.price;
  obj.directDC  = getAddOnDiscount(prodId, variantId);
  if (obj.checkStatus) window.myCustomProductAddCartClassConstructor?.addAddOnEventListenerFn?.();
  renderAddOnDiscount(false, 'noDialog', prodId);
  initChangeForXFAndFormBtnFn(); // 触发悬浮加购按钮和表单加购按钮的变体切换逻辑
};

// 外部全部 AddOn 加购按钮点击
window.myCustomAddOnCartBtnClickFn = () => {
  const cur = window.myCurrentProductAddOnObj;
  if (!cur?.available || !cur?.variantId) return;

  const items = [{ id: cur.variantId, quantity: cur.quantity || 1 }];
  Object.entries(window.myProductVariantAddOnObj).forEach(([, v]) => {
    if (v.checkStatus && v.variantId) {
      items.push({ id: v.variantId, quantity: v.quantity || 1 });
    }
  });
  if (!items.length) return;

  addToCart({ items });
  // 清空选中状态
  Object.values(window.myProductVariantAddOnObj).forEach((v) => { v.checkStatus = false; });
  document.querySelectorAll('.product-template-my-AddOn-prodlist-item')
    .forEach((el) => el.classList.remove('my-AddOn-prodlist-item-active'));
};

/* ──── AddOn 弹窗相关 ──── */
// AddOn 弹窗蒙层点击（关闭弹窗）
window.myCustomProdAddOnProductDetailMaskClickFn = (dom, event) => {
  event.stopPropagation();
  const id = dom.dataset.id;
  dom.closest('.product-template-my-AddOn-prodlist-item-wrap')
     ?.querySelector('.product-template-my-AddOn-prodlist-dialogitem')
     ?.style.setProperty('display', 'none');
  if (window.myProductVariantAddOnDialogObj[id]) {
    window.myProductVariantAddOnDialogObj[id].openStatus = false;
  }
  toggleAddOnMobileCart(true);
};

// AddOn 详情弹窗打开 / 关闭
window.myCustomProdAddOnProductDetailClickFn = (dom, event, flag) => {
  event.stopPropagation();
  const id = dom.dataset.id;
  dom.closest('.product-template-my-AddOn-prodlist-item-wrap')
     ?.querySelector('.product-template-my-AddOn-prodlist-dialogitem')
     ?.style.setProperty('display', flag ? 'block' : 'none');
  if (window.myProductVariantAddOnDialogObj[id]) {
    window.myProductVariantAddOnDialogObj[id].openStatus = !!flag;
  }
  toggleAddOnMobileCart(!flag);
};

// 手机端打开弹窗时隐藏悬浮购物车
const toggleAddOnMobileCart = (show) => {
  if (!isMobile()) return;
  const cartWrap = document.querySelector('.product-template-my-AddOn-form-wrap');
  if (cartWrap) cartWrap.style.visibility = show ? 'visible' : 'hidden';
};

// AddOn 弹窗内变体 select 展开（阻止冒泡）
window.myCustomAddOnDialogProdItemSelectVariantFn = (_dom, event) => {
  event.stopPropagation();
};

// AddOn 弹窗内变体切换
window.myAddOnDialogCustomOnChangeVariant = (variantId, dom) => {
  const selected = dom.querySelector(':selected');
  if (!selected) return;

  const prodId      = selected.dataset.prodId;
  const imgSrc      = selected.dataset.imgsrc;
  const price       = selected.dataset.variantSingleMp;
  const comparePrice = selected.dataset.variantSingleComparemp;

  const dialogItem = dom.closest('.product-template-my-AddOn-prodlist-dialogitem');
  dialogItem?.querySelector('.product-template-my-AddOn-prodlist-dialog-imgwrap img')
             ?.setAttribute('src', '/cdn/shop/' + imgSrc);
  if (dialogItem?.querySelector('.product-template-my-AddOn-prodlist-dialog-price-red'))
    dialogItem.querySelector('.product-template-my-AddOn-prodlist-dialog-price-red').textContent = price;
  if (dialogItem?.querySelector('.product-template-my-AddOn-prodlist-dialog-price-line'))
    dialogItem.querySelector('.product-template-my-AddOn-prodlist-dialog-price-line').textContent = comparePrice;

  if (!window.myProductVariantAddOnDialogObj[prodId]) return;
  const variant = window.myProductAddOnOriginObj[prodId]?.variants
    ?.find((v) => String(v.id) === String(variantId));
  if (!variant) return;

  const obj = window.myProductVariantAddOnDialogObj[prodId];
  obj.variantId = variantId;
  obj.directDC  = getAddOnDiscount(prodId, variantId);
  renderAddOnDiscount(false, 'dialog', prodId);
  renderAddOnDialogCartBtnText(false, prodId);
};

// AddOn 弹窗数量增减
window.myCustomProdAddOnDialogQuantityClickFn = (dom, event, isAdd) => {
  event.stopPropagation();
  const prodId = dom.parentElement?.dataset.prodId;
  if (!prodId || !window.myProductVariantAddOnDialogObj[prodId]?.variantId) return;

  const obj = window.myProductVariantAddOnDialogObj[prodId];
  let qty = obj.quantity || 1;
  if (isAdd) qty = Math.min(qty + 1, 100);
  else       qty = Math.max(qty - 1, 1);
  obj.quantity = qty;

  // 等 DOM 确认更新后再写入（微任务）
  Promise.resolve().then(() => {
    dom.parentElement.querySelector('.product-template-my-AddOn-prodlist-dialog-num-value')
       && (dom.parentElement.querySelector('.product-template-my-AddOn-prodlist-dialog-num-value').textContent = qty);
  });
};

// AddOn 弹窗内单个加购
window.myCustomSingleAddOnProdAddCartClickFn = () => {
  const items = [];
  Object.entries(window.myProductVariantAddOnDialogObj).forEach(([prodId, v]) => {
    if (v.openStatus && v.variantId) {
      items.push({
        id:         v.variantId,
        quantity:   v.quantity || 1,
        properties: { _customAddon: window.myProductVariantAddOnObj[prodId]?.variantId ?? '' },
      });
    }
  });
  if (items.length) addToCart({ items });
};

// 初始化：绑定原生加购按钮的 AddOn 加购逻辑
const bindAddOnOriginCartBtn = () => {
  const btn = document.getElementById('myCustomPordTemplateAddonClickBtn');
  btn?.addEventListener('click', () => {
    window.myCustomProductAddCartClassConstructor?.myCustomAddOnCartBtnClickFn?.();
  });
};

/** end-- addon相关 */
